import menuIcon from 'assets/images/icons/button/menu.svg';
import pdfDownloadIcon from 'assets/images/icons/button/pdf-download.svg';
import { ActionImageGreenButton, ActionPrimaryButton, CompilerActionSecondaryButton } from 'components/v2/buttons';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import ConfirmEditorialSendToPublishDialog from 'components/v2/dialogs/ConfirmEditorialSendToPublishDialog';
import ConfirmEditorialSendToPublishProcessCertificationDialog from 'components/v2/dialogs/ConfirmEditorialSendToPublishProcessCertificationDialog';
import { ConfirmWithCommentPayload } from 'components/v2/dialogs/ConfirmWithCommentDialog';
import DeclinePublicationDialog from 'components/v2/dialogs/DeclinePublicationDialog';
import DeclineVerificationDialog from 'components/v2/dialogs/DeclineVerificationDialog';
import DepublishAdminDialog, { DepublishWithReasonSelectionPayload } from 'components/v2/dialogs/DepublishAdminDialog';
import { toaster } from 'components/v2/toast';
import UpdateEpdTypeSelectionDialog from 'components/v2/dialogs/UpdateEpdTypeSelectionDialog';
import { ProcessStatus } from 'constants/constants';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { ActionState } from 'services/EpdClient';
import EpdStateService from 'services/EpdStateService';
import FileService from 'services/FileService';

import {
  useApproveVerification,
  useCancelApproveVerificationMutation,
  useCancelEpdDeregistration,
  useCancelPublicationEpd,
  useCancelUpdateEpd,
  useCancelVerification,
  useDeclinePublicationEpd,
  useDeclineVerification,
  useDepublishEpd,
  useDeregisterEpd,
  useEditorialUpdating,
  usePublishEpd,
  useSendEpdForPublicationEditorial,
  useSendEpdForPublication,
  useStartVerification,
  useUnlockEpd,
  useLicenseeAdminSendToPublish,
} from 'services/api/mutations';
import styled from 'styled-components';
import { CompilerActionsStatusModel, EPDPublicationProcessingTimeframeModel } from 'types/types';
import { formatUtcDateWithOffset, PoorMansError } from 'util/utils';
import axios, { AxiosError } from 'axios';
import { EPDDefinitionModel } from 'types/epd-definition/EPDDefinitionModel';
import LicenseeAdminReviewDialog from '../dialogs/LicenseeAdminReviewDialog';
import CompilerSurveyDialog from './CompilerSurveyDialog';
import useCompilerSurveyOffer from './useCompilerSurveyOffer';
import { getEpdPDFDocument, getEPDPublicationProcessingTimeFrame } from 'services/api/epdApi';
import { toast } from 'react-toastify';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { ACCESS_DENIED_DETAIL, getEpdActionErrorMessage, getEpdWriteBlockedMessage } from 'util/epdActionErrorUtils';

type VisibleDialogType =
  | 'unlock'
  | 'declineVerification'
  | 'confirmEditorialSendToPublish'
  | 'declinePublication'
  | 'depublishByAdmin'
  | 'licenseeAdminReviewDialog';

type Props = {
  epdId: string;
  epdVersionId: string;
  epdDefinition: EPDDefinitionModel;
  actionsStates: CompilerActionsStatusModel;
  onActionStatusChange(newStatus: ProcessStatus): void;
};

const CompilerToolButtons: React.FC<Props> = ({
  epdId,
  epdVersionId,
  epdDefinition,
  actionsStates,
  onActionStatusChange,
}) => {
  const [visibleDialog, setVisibleDialog] = useState<VisibleDialogType | null>(null);
  const { surveyInvitation, isSurveyVisible, offerCompilerSurvey, closeSurvey } = useCompilerSurveyOffer(epdId);
  const [epdUpdateType, setEpdUpdateType] = useState<'update' | 'editorialUpdate'>('update');
  const actionMenuRef = useRef<Menu>(null);
  const { t } = useTranslation();
  const history = useHistory();
  const { licensee } = useContext(CompanyContext);

  const cancelUpdateEpdMutation = useCancelUpdateEpd(epdId, epdVersionId);
  const startVerificationMutation = useStartVerification(epdId, epdVersionId);
  const cancelVerificationMutation = useCancelVerification(epdId, epdVersionId);
  const sendEpdForPublicationMutation = useSendEpdForPublication(epdId, epdVersionId);
  const cancelPublicationEpdMutation = useCancelPublicationEpd(epdId, epdVersionId);
  const deregisterEpdMutation = useDeregisterEpd(epdId, epdVersionId);
  const cancelEpdDeregistrationMutation = useCancelEpdDeregistration(epdId, epdVersionId);
  const unlockEpdMutation = useUnlockEpd(epdId, epdVersionId);
  const approveVerificationMutation = useApproveVerification(epdId, epdVersionId);
  const declineVerificationMutation = useDeclineVerification(epdId, epdVersionId);
  const cancelApproveVerificationMutation = useCancelApproveVerificationMutation(epdId, epdVersionId);
  const publishEpdMutation = usePublishEpd(epdId, epdVersionId);
  const declinePublicationMutation = useDeclinePublicationEpd(epdId, epdVersionId);
  const depublishEpdMutation = useDepublishEpd(epdId, epdVersionId);
  const sendEpdForPublicationEditorialMutation = useSendEpdForPublicationEditorial(epdId, epdVersionId);
  const editorialUpdatingMutation = useEditorialUpdating(epdId, epdVersionId);
  const licenseeAdminSendToPublishMutation = useLicenseeAdminSendToPublish(epdId, epdVersionId);

  // The backend names the version the Publish action publishes: while an update is in progress it can be an older,
  // depublished version (republish). Depublish acts on the published version. Neither needs to be the version on
  // screen, so their texts name the EPD without a version (the full number ends with the on-screen ":002").
  // The depublish texts show no title either: the title on screen is the newest version's, and an update can change it.
  const isRepublish = !!actionsStates.publishTargetVersionId && actionsStates.publishTargetVersionId !== epdVersionId;
  const epdNumberWithoutVersion = epdDefinition?.shortIdentificationNumber;

  const onAcceptCancelUpdateEpd = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await cancelUpdateEpdMutation.mutateAsync();
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.epdCancelled') as string,
      });
      window.location.reload();
    } catch (error) {
      let errorMessage =
        getEpdWriteBlockedMessage(error) ?? 'EPD update cancellation has not been accomplished due to unexpected error.';
      onActionStatusChange(ProcessStatus.Error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 403) {
          errorMessage = 'Only the EPD Owner is allowed to cancel the EPD update.';
        }
      }
      toaster({
        severity: 'error',
        summary: 'Error',
        details: errorMessage,
      });
    }
  };

  const onCancelUpdateEpd = () =>
    confirmAlert({
      caption: t('confirmModal.cancelUpdateEPD', {
        name: `${epdDefinition.fullIdentificationNumber} ${epdDefinition.declarationName}`,
      }),
      description: <div>All added changes will be lost.</div>,
      acceptLabel: t('Submit'),
      rejectLabel: t('Cancel'),
      onConfirm: onAcceptCancelUpdateEpd,
    });

  const onAcceptDeleteEpd = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await EpdStateService.delete(epdId);
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.epdDeleted', {
          epdNumber: epdDefinition.fullIdentificationNumber,
        }) as string,
      });
      history.push(EpdLinks.dashboard());
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      const epdErrorMsg = getEpdActionErrorMessage(error);
      if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
        toaster({ severity: 'error', summary: 'Access denied', details: epdErrorMsg });
        history.push(EpdLinks.dashboard());
        return;
      }
      const errorMessage = epdErrorMsg ?? 'EPD has not been deleted due to unexpected error.';
      toaster({ severity: 'error', summary: 'Error', details: errorMessage });
      console.error(error);
    }
  };

  const onDeleteEpd = () =>
    confirmDelete({
      caption: t('confirmModal.deleteEPD', {
        name: `${epdDefinition.fullIdentificationNumber} ${epdDefinition.declarationName}`,
      }),
      description: <div>The EPD data will be lost and you will not be able to restore it.</div>,
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: onAcceptDeleteEpd,
    });

  const onStartVerification = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await startVerificationMutation.mutateAsync();
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.startVerification') as string,
      });
      offerCompilerSurvey('StartVerification');
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? 'EPD verification has not been started due to unexpected error.',
      });
    }
  };

  const onCancelVerification = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await cancelVerificationMutation.mutateAsync();
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.cancelVerification') as string,
      });
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? 'EPD verification has not been canceled due to unexpected error.',
      });
    }
  };

  const onCancelVerificationEpd = () =>
    confirmAlert({
      caption: t('confirmModal.cancelEPDVerification', {
        name: `${epdDefinition.fullIdentificationNumber} ${epdDefinition.declarationName}`,
      }),
      description: <div>This action may interrupt Step 4 - EPD verification process.</div>,
      acceptLabel: 'Submit',
      rejectLabel: 'Cancel',
      onConfirm: onCancelVerification,
    });

  const sendForPublishToastTemplate = (
    timeframe: EPDPublicationProcessingTimeframeModel | undefined,
    hasLicensee: boolean
  ) => {
    return (
      <>
        <div>
          <SendForPublishSuccessNotificationTitle>
            Thank you for registering your EPD with the International EPD System!
          </SendForPublishSuccessNotificationTitle>
          <SendForPublishSuccessNotificationParagraph>
            {hasLicensee ? (
              <>
                We will attend to it as soon as possible. We aim to publish the EPD within {timeframe?.minProcessingDays}-
                {timeframe?.maxProcessingDays} working days after the initial review by the Licensee. If you have any
                questions, feel free to contact the Secretariat at support@environdec.com.
              </>
            ) : (
              <>
                We will attend to it as soon as possible. We aim to publish the EPD within {timeframe?.minProcessingDays}-
                {timeframe?.maxProcessingDays} working days. If you have any questions, feel free to contact the Secretariat
                at support@environdec.com.
              </>
            )}
          </SendForPublishSuccessNotificationParagraph>
          <SendForPublishSuccessNotificationParagraph>
            If any information is missing or if any irregularities are recognized in the submitted material, the Secretariat
            will contact you.
          </SendForPublishSuccessNotificationParagraph>
        </div>
      </>
    );
  };

  const onSendEpdForPublication = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await sendEpdForPublicationMutation.mutateAsync();
      onActionStatusChange(ProcessStatus.Success);
      const timeframe = await getEPDPublicationProcessingTimeFrame();
      toast.success(sendForPublishToastTemplate(timeframe, licensee?.name !== 'defaultLicensee'));
      offerCompilerSurvey('SendToPublish');
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      const epdErrorMsg = getEpdActionErrorMessage(error);
      if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
        toaster({ severity: 'error', summary: 'Access denied', details: epdErrorMsg });
        history.push(EpdLinks.dashboard());
        return;
      }
      toaster({
        severity: 'error',
        summary: 'Error',
        details: epdErrorMsg ?? 'EPD has not been registered due to unexpected error.',
      });
    }
  };

  const onCancelPublicationClicked = () => {
    if (!epdDefinition) {
      return;
    }
    confirmAlert({
      caption: t('confirmModal.cancelPublicationEPD', {
        name: `${epdDefinition.fullIdentificationNumber} ${epdDefinition.declarationName}`,
      }),
      description: 'You may interrupt the EPD reviewing process.',
      acceptLabel: t('Submit'),
      onConfirm: onCancelPublicationConfirmed,
    });
  };

  const onCancelPublicationConfirmed = async () => {
    if (!epdDefinition) {
      return;
    }
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await cancelPublicationEpdMutation.mutateAsync();
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.canceledPublicationEpd', {
          name: `${epdDefinition.fullIdentificationNumber} ${epdDefinition.declarationName}`,
        }) as string,
      });
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      const epdErrorMsg = getEpdActionErrorMessage(error);
      if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
        toaster({ severity: 'error', summary: 'Access denied', details: epdErrorMsg });
        history.push(EpdLinks.dashboard());
        return;
      }
      toaster({
        severity: 'error',
        summary: 'Error',
        details: epdErrorMsg ?? 'EPD publication has not been canceled due to unexpected error.',
      });
    }
  };

  const onConfirmDeregisterEpd = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await deregisterEpdMutation.mutateAsync();
      onActionStatusChange(ProcessStatus.Success);
      history.go(0);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.epdDeregister', {
          epdNumber: epdDefinition.fullIdentificationNumber,
        }) as string,
      });
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      const epdErrorMsg = getEpdActionErrorMessage(error);
      if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
        toaster({ severity: 'error', summary: 'Access denied', details: epdErrorMsg });
        history.push(EpdLinks.dashboard());
        return;
      }
      toaster({
        severity: 'error',
        summary: 'Error',
        details: epdErrorMsg ?? 'EPD has not been deregistered due to unexpected error.',
      });
    }
  };

  const onDeregisterEpd = () =>
    confirmDelete({
      caption: t('confirmModal.deregisterEPD', {
        name: `${epdDefinition.fullIdentificationNumber} ${epdDefinition.declarationName}`,
      }),
      description: <div>The EPD will be deregistered and shall no longer be used or referred to.</div>,
      acceptLabel: t('deregister'),
      rejectLabel: t('cancel'),
      onConfirm: onConfirmDeregisterEpd,
    });

  const onCancelEpdDeregistration = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await cancelEpdDeregistrationMutation.mutateAsync();
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.cancelDeregistration') as string,
      });
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details:
          getEpdWriteBlockedMessage(error) ??
          'Cancelation of EPD deregistration has not been accomplished due to unexpected error.',
      });
    }
  };

  const onStartUpdatingEpd = async () => {
    if (actionsStates.isVerifierInvolved) {
      setVisibleDialog('unlock');
      return;
    }

    startUpdatingHandler('editorialUpdate');
  };

  const startUpdatingHandler = async (updateType: 'update' | 'editorialUpdate') => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);

      if (updateType === 'update') {
        await unlockEpdMutation.mutateAsync();
      } else if (updateType === 'editorialUpdate') {
        await editorialUpdatingMutation.mutateAsync();
      }
      onActionStatusChange(ProcessStatus.Success);
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? 'Updating of EPD has not been started due to unexpected error.',
      });
    }
  };

  const handleEditorialSendToPublishConfirmed = (payload: ConfirmWithCommentPayload | null) => {
    setVisibleDialog(null);
    if (!payload) {
      return;
    }
    sendForPublicationEditorialHandler(payload);
  };

  const sendForPublicationEditorialHandler = async (payload: ConfirmWithCommentPayload) => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await sendEpdForPublicationEditorialMutation.mutateAsync({ epdId, notes: payload.commentText });
      onActionStatusChange(ProcessStatus.Success);
      const timeframe = await getEPDPublicationProcessingTimeFrame();
      toast.success(sendForPublishToastTemplate(timeframe, licensee?.name !== 'defaultLicensee'));
      offerCompilerSurvey('SendToPublish');
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      const epdErrorMsg = getEpdActionErrorMessage(error);
      if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
        toaster({ severity: 'error', summary: 'Access denied', details: epdErrorMsg });
        history.push(EpdLinks.dashboard());
        return;
      }
      toaster({
        severity: 'error',
        summary: 'Error',
        details: epdErrorMsg ?? 'Updating of EPD has not been started due to unexpected error.',
      });
    }
  };

  const onApproveVerificationEpd = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      const currentDateTimeWithTimezone = formatUtcDateWithOffset(new Date());
      const approveModel = new FormData();
      approveModel.append('LastRevisionDate', currentDateTimeWithTimezone);
      await approveVerificationMutation.mutateAsync(approveModel);
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.approvedVerification', {
          epdNumber: epdDefinition.fullIdentificationNumber,
        }) as string,
      });
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? 'Approval of EPD has not been accomplished due to unexpected error.',
      });
    }
  };

  const onDeclineVerificationEpd = async (payload: ConfirmWithCommentPayload) => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await declineVerificationMutation.mutateAsync({ reason: payload.commentText });
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.declinedVerification', {
          epdNumber: epdDefinition.fullIdentificationNumber,
        }) as string,
      });
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? 'Rejection of EPD has not been accomplished due to unexpected error.',
      });
    }
  };

  const onRetractVerification = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await cancelApproveVerificationMutation.mutateAsync();
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.retractVerification') as string,
      });
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details:
          getEpdWriteBlockedMessage(error) ??
          'Retraction of EPD verification has not been accomplished due to unexpected error.',
      });
    }
  };

  const onPublishEpd = async () => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      const result = await publishEpdMutation.mutateAsync(actionsStates.publishTargetVersionId ?? epdVersionId);
      onActionStatusChange(ProcessStatus.Success);
      if (result?.message) {
        toaster({ severity: 'info', details: result.message });
      } else {
        toaster({
          severity: 'success',
          summary: 'Success',
          details: t('messages.epdPublished', {
            epdNumber: isRepublish ? epdNumberWithoutVersion : epdDefinition.fullIdentificationNumber,
          }) as string,
        });
      }
    } catch (error: any) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: error?.response?.data?.detail || 'Publication of EPD has not been accomplished due to unexpected error.',
      });
    }
  };

  const onDeclinePublicationEpd = async (payload: ConfirmWithCommentPayload) => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await declinePublicationMutation.mutateAsync({
        declineReason: payload.commentText,
      });
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.declinedPublication', {
          epdNumber: epdDefinition?.fullIdentificationNumber,
        }) as string,
      });
    } catch (error) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? 'Rejection of EPD has not been accomplished due to unexpected error.',
      });
    }
  };

  const onDepublishEpd = async (payload?: DepublishWithReasonSelectionPayload) => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await depublishEpdMutation.mutateAsync({
        depublicationReason: payload ? payload.commentText : null,
        depublishType: payload ? payload.depublishType : null,
      });
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: t('messages.epdDepublished', { epdNumber: epdNumberWithoutVersion }) as string,
      });
    } catch (error: any) {
      onActionStatusChange(ProcessStatus.Error);
      const epdErrorMsg = getEpdActionErrorMessage(error);
      if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
        toaster({ severity: 'error', summary: 'Access denied', details: epdErrorMsg });
        history.push(EpdLinks.dashboard());
        return;
      }
      toaster({
        severity: 'error',
        summary: 'Error',
        details:
          epdErrorMsg ??
          error?.response?.data?.detail ??
          'Depublish of EPD has not been accomplished due to unexpected error.',
      });
    }
  };

  const onDepublishByOwnerEpd = () =>
    confirmAlert({
      caption: t('confirmModal.depublishEPDByOwner', {
        name: epdNumberWithoutVersion,
      }),
      description: (
        <div>
          The EPD will be depublished and no longer available on environdec.com.
          <br /> To publish your EPD again, you will need to contact the Secretariat.
        </div>
      ),
      acceptLabel: 'Depublish',
      rejectLabel: 'Cancel',
      onConfirm: () => onDepublishEpd(),
    });

  const handleLicenseeAdminSentToPublish = async ({ commentText }: ConfirmWithCommentPayload) => {
    try {
      onActionStatusChange(ProcessStatus.Fetching);
      await licenseeAdminSendToPublishMutation.mutateAsync({ licenseeAdminReviewComment: commentText });
      onActionStatusChange(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: 'Operation completed successfully!',
      });
    } catch (error: any) {
      onActionStatusChange(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? PoorMansError(error),
      });
    }
  };

  const handleDownloadPDF = async () => {
    const pdfDocument = await getEpdPDFDocument(epdVersionId);
    if (pdfDocument?.id) {
      window.open(FileService.getImageUrl(pdfDocument.id), '_blank', 'noreferrer');
    } else {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: 'The EPD document in PDF format was not found, please contact Secretariat.',
      });
    }
  };

  const actionMenuItems: MenuItem[] = [
    {
      label: 'Depublish EPD',
      command: () => setVisibleDialog('depublishByAdmin'),
      visible: actionsStates.depublishByAdmin === ActionState.Enabled,
    },
    {
      label: 'Depublish EPD',
      command: onDepublishByOwnerEpd,
      visible: actionsStates.depublishByCustomer === ActionState.Enabled,
    },
    {
      label: 'Deregister EPD',
      command: onDeregisterEpd,
      visible: actionsStates.deregister === ActionState.Enabled,
    },
    {
      label: 'Preview PDF',
      url: EpdLinks.epdVersionDocumentPreview(epdVersionId),
      target: '_blank',
      visible: actionsStates.previewPdf === ActionState.Enabled,
    },
    {
      label: 'Download PDF',
      command: () => {
        handleDownloadPDF();
      },
      visible: actionsStates.downloadPdf === ActionState.Enabled,
    },
  ];

  const epdDetailsForDialogs = epdDefinition
    ? {
        fullIdentificationNumber: epdDefinition.fullIdentificationNumber,
        title: epdDefinition.declarationName,
      }
    : null;

  const epdDetailsForDepublishDialog = epdDefinition
    ? {
        epdNumber: epdNumberWithoutVersion,
      }
    : null;

  const handlePreviewPDF = () => {
    window.open(EpdLinks.epdVersionDocumentPreview(epdVersionId), '_blank');
  };

  const visibleActionMenuItems = actionMenuItems.filter((m) => m.visible);

  return (
    <>
      <CompilerActionSecondaryButton
        actionState={actionsStates.cancelUpdate}
        label="Cancel update"
        onClick={onCancelUpdateEpd}
        outlined
        type="reset"
      />
      <CompilerActionSecondaryButton
        actionState={actionsStates.deleteDraft}
        label="Delete EPD"
        onClick={onDeleteEpd}
        outlined
        type="reset"
      />
      <CompilerActionSecondaryButton
        actionState={actionsStates.declineVerification}
        label="Decline EPD"
        onClick={() => setVisibleDialog('declineVerification')}
        outlined
      />
      <CompilerActionSecondaryButton
        actionState={actionsStates.declinePublication}
        label="Decline EPD"
        onClick={() => setVisibleDialog('declinePublication')}
        outlined
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Start verification"
        onClick={onStartVerification}
        actionState={actionsStates.startVerification}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Cancel verification"
        onClick={onCancelVerificationEpd}
        actionState={actionsStates.cancelVerification}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Cancel publication"
        onClick={onCancelPublicationClicked}
        actionState={actionsStates.cancelPublication}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Send to publish"
        onClick={onSendEpdForPublication}
        actionState={actionsStates.sendForPublication}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Cancel deregistration"
        onClick={onCancelEpdDeregistration}
        actionState={actionsStates.cancelDeregistration}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Update EPD"
        onClick={onStartUpdatingEpd}
        actionState={actionsStates.unlock}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Send to publish"
        onClick={() => setVisibleDialog('confirmEditorialSendToPublish')}
        actionState={actionsStates.sendForPublicationEditorial}
      />
      {actionsStates.isVerifierInvolved ? (
        <ConfirmEditorialSendToPublishDialog
          isOpened={visibleDialog === 'confirmEditorialSendToPublish'}
          onClose={handleEditorialSendToPublishConfirmed}
        />
      ) : (
        <ConfirmEditorialSendToPublishProcessCertificationDialog
          isOpened={visibleDialog === 'confirmEditorialSendToPublish'}
          onClose={handleEditorialSendToPublishConfirmed}
        />
      )}
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Retract Verification"
        onClick={onRetractVerification}
        actionState={actionsStates.retractVerification}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Approve EPD"
        onClick={onApproveVerificationEpd}
        actionState={actionsStates.approveVerification}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label={isRepublish ? 'Republish EPD' : 'Publish EPD'}
        onClick={onPublishEpd}
        actionState={actionsStates.publish}
      />
      <ActionPrimaryButton
        style={{ marginLeft: '16px' }}
        label="Send to publish"
        onClick={() => setVisibleDialog('licenseeAdminReviewDialog')}
        actionState={actionsStates.licenseeAdminSendToPublish}
      />
      {visibleActionMenuItems.length > 1 ||
      (visibleActionMenuItems.length === 1 && visibleActionMenuItems[0].label === 'Download PDF') ? (
        <ActionImageGreenButton
          style={{ marginLeft: '16px' }}
          onClick={(e: any) => actionMenuRef.current?.toggle(e)}
          actionState={ActionState.Enabled}
        >
          <img alt="menu" src={menuIcon} />
        </ActionImageGreenButton>
      ) : (
        actionsStates?.previewPdf === ActionState.Enabled && (
          <ActionImageGreenButton
            style={{ marginLeft: '16px' }}
            onClick={handlePreviewPDF}
            actionState={ActionState.Enabled}
          >
            <img alt="menu" src={pdfDownloadIcon} />
          </ActionImageGreenButton>
        )
      )}

      <Menu ref={actionMenuRef} model={actionMenuItems} popupAlignment={'right'} popup />
      <UpdateEpdTypeSelectionDialog
        visible={visibleDialog === 'unlock'}
        updateType={epdUpdateType}
        onUpdateTypeChange={(value) => setEpdUpdateType(value)}
        onAccept={() => startUpdatingHandler(epdUpdateType)}
        onHide={() => setVisibleDialog(null)}
      />
      <DeclineVerificationDialog
        isOpened={visibleDialog === 'declineVerification'}
        epd={epdDetailsForDialogs}
        onClose={(payload) => {
          setVisibleDialog(null);
          if (!payload) {
            return;
          }
          onDeclineVerificationEpd(payload);
        }}
      />
      <DeclinePublicationDialog
        isOpened={visibleDialog === 'declinePublication'}
        epd={epdDetailsForDialogs}
        onClose={(payload) => {
          setVisibleDialog(null);
          if (!payload) {
            return;
          }
          onDeclinePublicationEpd(payload);
        }}
      />
      <DepublishAdminDialog
        isOpened={visibleDialog === 'depublishByAdmin'}
        epd={epdDetailsForDepublishDialog}
        onClose={(payload) => {
          setVisibleDialog(null);
          if (!payload) {
            return;
          }
          onDepublishEpd(payload);
        }}
      />
      <LicenseeAdminReviewDialog
        isOpened={visibleDialog === 'licenseeAdminReviewDialog'}
        onClose={(payload) => {
          setVisibleDialog(null);
          if (!payload) {
            return;
          }
          handleLicenseeAdminSentToPublish(payload);
        }}
      />
      {surveyInvitation && (
        <CompilerSurveyDialog
          key={surveyInvitation.id}
          visible={isSurveyVisible}
          trigger={surveyInvitation.trigger}
          surveyUrl={surveyInvitation.surveyUrl}
          onClose={closeSurvey}
        />
      )}
    </>
  );
};

export default CompilerToolButtons;

const SendForPublishSuccessNotificationTitle = styled.b`
  font-family: Inter;
  font-weight: 600;
  line-height: 20px;
`;

const SendForPublishSuccessNotificationParagraph = styled.p`
  font-family: Inter;
  font-weight: 400;
  line-height: 20px;
  opacity: 70%;
  margin-top: 10px;
  margin-bottom: 0px;
`;
